import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import styled from 'styled-components'

import { fetchApi } from '@hozana/api/fetchApi'
import { useAttributes } from '@hozana/dom/hooks/useAttributes'
import { useDispatch } from '@hozana/redux/hooks'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'

import { Button } from 'elements/button/Button'
import { RoundButton, TRoundButtonProps } from 'elements/button/RoundButton'
import { Input } from 'elements/input/Input'
import { Div } from 'elements/layout/Div'

import { ICON } from 'config/icons'
import { addFlash } from 'general/actions'

import { SHARE_OBJECTS, SHARE_SOCIAL_SIZE, SHARE_TYPES } from 'modules/share/constants'
import { useShare } from 'modules/share/hook/useShare'
import { incrementSharesMutation } from 'modules/share/queries'

const StyledInput = styled(Input)`
  border-radius: 3px 0 0 3px;
  border-right: none;
`

const StyledButton = styled(Button)`
  border-radius: 0 3px 3px 0;
`

export type TShareCopyProps = {
  expanded?: boolean
  keepExpandedOnCopy?: boolean
  onExpand?: () => void
  onShare?: () => void
  classicButton?: boolean
} & Pick<TRoundButtonProps, 'm' | 'size'>

export const ShareCopy: TFC<TShareCopyProps> = ({
  expanded = false,
  size = SHARE_SOCIAL_SIZE.REGULAR,
  m,
  keepExpandedOnCopy,
  onExpand,
  onShare,
  classicButton,
}) => {
  const [copyPasteUrl, setCopyPasteUrl] = useState(null)
  const [isExpanded, expand] = useState(false)
  const attrs = useAttributes()
  const share = useShare()
  const dispatch = useDispatch()
  const screen = useScreen()

  const generateCopyPasteInviteLink = useCallback(() => {
    // we display input + button to copy
    expand(true)
    // provided function
    onExpand?.()
    // if URL already exists, don't regenerate a new one
    if (!copyPasteUrl) {
      share.generateInviteLink(SHARE_TYPES.COPY).then((url) => {
        console.log(url)
        setCopyPasteUrl(url)
      })
    }
  }, [copyPasteUrl, expand, share, onExpand])

  // if expanded is true, we set expand to display directly the input + button to copy
  useEffect(() => {
    if (expanded && !isExpanded && share) {
      generateCopyPasteInviteLink()
    }
  }, [expanded, isExpanded, generateCopyPasteInviteLink, share])

  const selectInputText: MouseEventHandler<HTMLInputElement> = (e) => {
    const input = e.target as HTMLInputElement
    if (screen.xs) {
      input.readOnly = false // iOS will not select in a read only form element
      input.setSelectionRange(0, input.value.length)
    } else {
      input.select()
    }
  }

  const onCopy = () => {
    const { track, objectToShare, community, publication } = share

    dispatch(
      addFlash({
        message: 'share:share.copy-link-success',
        subMessage: screen.xs ? undefined : 'share:share.copy-paste-link-explanations',
      }),
    )

    // increment share for whatsapp|messenger|copylink|email
    if (objectToShare === SHARE_OBJECTS.COMMUNITY || objectToShare === SHARE_OBJECTS.PUBLICATION) {
      dispatch(
        fetchApi(
          incrementSharesMutation({
            objectKey: `/${objectToShare}/${objectToShare === SHARE_OBJECTS.COMMUNITY ? community.id : publication.id}`,
            shareType: SHARE_TYPES.COPY_LINK,
          }),
        ),
      )
    }

    // Send GA event
    track(SHARE_TYPES.COPY)

    // reset input
    if (!keepExpandedOnCopy) setCopyPasteUrl(null)
    // display initial button
    expand(keepExpandedOnCopy)
    // provided function
    onShare?.()
  }

  return (
    <Div minH="40px">
      {!isExpanded &&
        !expanded &&
        (classicButton ? (
          <Button
            w="100%"
            onClick={generateCopyPasteInviteLink}
            data-testid={QA.SHARE_COMMON.SOCIAL_BUTTON(SHARE_TYPES.COPY)}
            icon={ICON.COPY}
          >
            trans:share:share.copy-link
          </Button>
        ) : (
          <RoundButton
            icon={ICON.COPY}
            onClick={generateCopyPasteInviteLink}
            m={m}
            size={size}
            bg="black50"
            color="white"
            hoverBg="black70"
            hoverColor="white"
            label="trans:share:share.copy-link"
            data-testid={QA.SHARE_COMMON.SOCIAL_BUTTON(SHARE_TYPES.COPY)}
            {...attrs}
          />
        ))}
      {isExpanded && copyPasteUrl && (
        <Div align={'middleLeft'}>
          <Div maxW="140px">
            <StyledInput color="black50" h="40px" onClick={selectInputText} value={copyPasteUrl} readOnly />
          </Div>
          <CopyToClipboard text={copyPasteUrl} onCopy={onCopy}>
            <StyledButton disabled={!copyPasteUrl} data-testid={QA.SHARE_COMMON.COPY_LINK_BUTTON}>
              trans:share:word.copy
            </StyledButton>
          </CopyToClipboard>
        </Div>
      )}
    </Div>
  )
}
